import { useContext, useState, useEffect, useRef } from 'react';
import { Combobox, Listbox, Text } from '@shopify/polaris';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { queryClient } from '#client/utils/queryClient.js';

const LocationSwitcher = () => {
    const { currentUser, updateSession, sessionId } = useContext(SessionContext);

    const allLocations = currentUser?.locations || [];
    const allLocationNames = currentUser?.locationNames || [];

    const [selectedId, setSelectedId] = useState(() => {
        return localStorage.getItem('activeStore') || 'ALL';
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermActive, setSearchTermActive] = useState(false);
    const [showCheckmark, setShowCheckmark] = useState(false);
    const inputWrapperRef = useRef(null);

    useEffect(() => {
        // Don't run this logic unless session is initialized
        if (!sessionId || !currentUser) return;

        // Do not proceed if user has no assigned locations
        if (currentUser?.locations == null || currentUser.locations.length === 0) return;

        // Retrieve the previously selected location from localStorage and current session
        const stored = localStorage.getItem('activeStore');
        const current = currentUser.activeStore;

        // Get a list of valid location IDs currently assigned to the user
        const validLocationIds = currentUser.locations.map((loc) => loc.locationId);

        // Check if the stored or current location ID is still valid
        const isStoredValid = stored && (stored === 'ALL' || validLocationIds.includes(stored));
        const isCurrentValid = current && (current === 'ALL' || validLocationIds.includes(current));

        if (isCurrentValid) {
            // Use the current session value if valid
            setSelectedId(current);
            localStorage.setItem('activeStore', current);
        } else if (isStoredValid) {
            // Fall back to the localStorage value if current session value is invalid
            setSelectedId(stored);
            localStorage.setItem('activeStore', stored);
        } else {
            // Neither value is valid (ie: location was removed), reset to "ALL"
            setSelectedId('ALL');
            localStorage.setItem('activeStore', 'ALL');

            // Attempt to update session, and report error if it fails
            updateSession({ activeStore: null }).catch((err) => {
                console.error('Error authenticating session (during reset to ALL):', err);
            });
        }
    }, [sessionId, currentUser]);

    useEffect(() => {
        setTimeout(() => {
            const inputEl = inputWrapperRef.current?.querySelector('input');
            if (inputEl instanceof HTMLElement) {
                inputEl.blur();
            }
        }, 0);
        queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
        queryClient.invalidateQueries({ queryKey: ['InventoryAdjustment'] });
    }, [selectedId]);

    // Sync location selection across browser tabs via localStorage events
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'activeStore' && event.newValue !== selectedId) {
                const newValue = event.newValue || 'ALL';
                setSelectedId(newValue);

                const payload = newValue === 'ALL' ? { activeStore: null } : { activeStore: newValue };
                updateSession(payload).catch((err) => {
                    console.error('Error syncing session from other tab:', err);
                });
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [selectedId, updateSession]);

    // Do not show if there are no assigned locations
    if (!currentUser || allLocations.length === 0) return null;

    // If only one location, show plain text
    if (allLocations.length === 1) {
        return (
            <Text fontWeight="medium" as="span" style={{ marginLeft: '1rem' }}>
                <strong>Location:</strong> {allLocationNames?.[0] || 'Unknown'}
            </Text>
        );
    }

    const handleSelect = async (value) => {
        setSelectedId(value);
        setSearchTerm('');
        localStorage.setItem('activeStore', value);

        const payload = value === 'ALL' ? { activeStore: null } : { activeStore: value };
        await updateSession(payload);

        setShowCheckmark(true);

        setTimeout(() => {
            const inputEl = inputWrapperRef.current?.querySelector('input');
            if (inputEl instanceof HTMLElement) {
                inputEl.blur();
            }
        }, 0);

        // Hide the checkmark after fade duration
        setTimeout(() => setShowCheckmark(false), 1000);
    };

    const allOptions = [
        {
            value: 'ALL',
            label: 'All Locations',
        },
        ...allLocations.map((loc, index) => ({
            value: loc.locationId,
            label: allLocationNames[index] || 'Unknown Location',
        })),
    ];

    const filteredOptions = allOptions.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()));

    const getSelectedLabel = () => {
        if (selectedId === 'ALL' || selectedId === null) {
            return 'All Locations';
        }

        const index = allLocations.findIndex((loc) => loc.locationId === selectedId);
        return index !== -1 ? allLocationNames[index] : 'Unknown Location';
    };

    return (
        <div className="location-switcher">
            <Combobox
                activator={
                    <div ref={inputWrapperRef} className="location-switcher__activator">
                        <Combobox.TextField
                            labelHidden
                            label="Locations"
                            value={searchTermActive ? searchTerm : getSelectedLabel()}
                            onChange={(val) => {
                                setSearchTerm(val);
                            }}
                            onFocus={() => setSearchTermActive(true)}
                            onBlur={() => {
                                // slight delay so clicking an item doesn't immediately close it
                                setTimeout(() => setSearchTermActive(false), 100);
                            }}
                            placeholder="Search or select a location"
                            autoComplete="off"
                            selectTextOnFocus={false}
                            suffix={
                                <span className={`location-switcher__checkmark ${showCheckmark ? 'visible' : ''}`} aria-hidden="true">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                                        <path d="M8.5 13.086l-3.793-3.793-1.414 1.414L8.5 15.914l9-9-1.414-1.414z" fill="#008060" />
                                    </svg>
                                </span>
                            }
                        />
                    </div>
                }
            >
                <Listbox onSelect={handleSelect} className="location-switcher__listbox">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => (
                            <Listbox.Option key={opt.value} value={opt.value} selected={selectedId === opt.value}>
                                {opt.label}
                            </Listbox.Option>
                        ))
                    ) : (
                        <Listbox.Option value="no_results" disabled>
                            No locations found
                        </Listbox.Option>
                    )}
                </Listbox>
            </Combobox>
        </div>
    );
};

export default LocationSwitcher;
